import { useMemo } from 'preact/hooks';
import type { WidgetQuickQuestion } from '@digital-cs/shared';
import { useI18n } from '../../hooks/use-i18n';
import { useQuickQuestionsRotation } from '../../hooks/use-grouped-rotation';
import { resolveQuickQuestionText } from '../../i18n/widget-content';
import { currentLocale } from '../../store/widget-store';

interface QuickQuestionCarouselProps {
  questions: WidgetQuickQuestion[];
  /** 点击某条引导问题。ChatPane → 发送;WelcomePane → 进入聊天。 */
  onPick: (text: string) => void;
  /** AI 回合进行中置 true:按钮禁用 + hover 提示,回合结束自动解锁 */
  disabled?: boolean;
  /** disabled 时的 hover 提示文案(不传则禁用态无 title) */
  disabledHint?: string;
}

/**
 * 引导提问分组轮播 —— 每 2 个一组左右并排,超过一组时整组向上轮播;
 * 单按钮文字过长时在按钮内跑马灯滚完全文,文本展示完成 + 停留后再切下一组。
 *
 * 分组/测量/跑马灯/推进/hover 暂停/reduced-motion 全部复用 useQuickQuestionsRotation
 * (固定 2 个一组 + 数字人主题选择器),视觉差异(向上滑动 vs web 翻牌)在 widget.css 实现。
 */
export function QuickQuestionCarousel({
  questions,
  onPick,
  disabled,
  disabledHint,
}: QuickQuestionCarouselProps) {
  const { dir } = useI18n();
  const locale = currentLocale.value; // 订阅 locale,切换后重新映射文本 → 重新分组/重测
  // 必须 memo:hook 内部 setState 会触发本组件重渲染,若每次都给新数组,
  // hook 的重置 effect(deps 含 items)会每渲染都重置(清定时器/回第0组)→ 永不轮播。
  const items = useMemo(
    // 非中文 locale 无译文的条目整条隐藏(resolveQuickQuestionText 返回 null),避免中英混杂
    () =>
      questions
        .map((q) => ({ id: q.id, text: resolveQuickQuestionText(q, locale) }))
        .filter((q): q is { id: string; text: string } => q.text !== null),
    [questions, locale],
  );

  const {
    currentBatch,
    chipMetrics,
    flipPhase,
    onChipHoverEnter,
    onChipHoverLeave,
    viewportRef,
  } = useQuickQuestionsRotation(items, {
    groupSize: 2,
    adaptiveBatch: false,
    wrapSelector: '.dcs-dq-chip-text-wrap',
    textSelector: '.dcs-dq-chip-text',
  });

  if (items.length === 0) return null;

  return (
    <div class="dcs-dq-viewport" ref={viewportRef} data-flip={flipPhase}>
      {currentBatch.map((item) => {
        const metric = chipMetrics[item.id];
        const isMarquee = !!metric;
        // LTR 文字溢出向左滚露尾(负);RTL 反向(正)
        const distance = metric ? (dir === 'rtl' ? metric.distance : -metric.distance) : 0;
        const style = isMarquee
          ? `--dcs-dq-distance:${distance}px;--dcs-dq-duration:${metric.duration}ms`
          : undefined;
        return (
          <button
            key={item.id}
            type="button"
            class={`dcs-dq-chip${isMarquee ? ' is-marquee' : ''}${disabled ? ' is-busy' : ''}`}
            style={style}
            // 不用原生 disabled:禁用的按钮不触发鼠标事件,title 提示不显示、
            // 也无法给点击反馈。aria-disabled + is-busy 样式,点击由 sendText 的
            // aiBusy 守卫拦截(并闪现忙碌提示)。
            aria-disabled={disabled || undefined}
            title={disabled && disabledHint ? disabledHint : undefined}
            onClick={() => onPick(item.text)}
            onMouseEnter={() => onChipHoverEnter(item.id)}
            onMouseLeave={onChipHoverLeave}
          >
            <span class="dcs-dq-chip-text-wrap" data-chip-id={item.id}>
              <span class="dcs-dq-chip-text">{item.text}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
